import React from "react";
import { Identifiable, Project, role as roleMock } from "@planzone/dto";
import {
  useAuthentication,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneRole,
  useRetrieveProjectRole,
  useRetrieveProjectsRoles,
} from "@planzone/view-models";

function usePlanzonePermissions() {
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    role,
    isRequestFailure,
    isRequestSuccess,
    isRequestPending,
    retrievePlanzoneRole,
  } = useRetrievePlanzoneRole();
  const { account, hasSession } = useAuthentication();

  React.useEffect(() => {
    hasSession &&
      !isRequestFailure &&
      !isRequestSuccess &&
      !isRequestPending &&
      retrievePlanzoneRole({ planzone, account });
  }, [planzone.uuid, hasSession]);

  return {
    isAdministrator: role.name === "pz_admin",
    isSubAdministrator: role.name === "pz_under_admin",
    isUser: role.name === "pz_user",
    isVisitor: role.name === "pz_visitor",
  };
}

function useProjectPermissions(project: Identifiable<Project>) {
  const {
    projectResources,
    isRequestFailure,
    isRequestSuccess,
    isRequestPending,
    retrieveProjectsRoles,
  } = useRetrieveProjectsRoles();
  const { role: planzoneRole } = useRetrievePlanzoneRole();
  const { account, hasSession } = useAuthentication();
  const isAdministrator = planzoneRole.name === "pz_admin";
  const projectResource = projectResources.find(
    (resource) => resource.project.uuid === project.uuid,
  );
  const role = !!projectResource ? projectResource.role : roleMock;

  React.useEffect(() => {
    hasSession &&
      !isAdministrator &&
      !isRequestFailure &&
      !isRequestSuccess &&
      !isRequestPending &&
      retrieveProjectsRoles(account);
  }, [hasSession]);

  return {
    isProjectManager: isAdministrator || role.name === "pr_manager",
    isObserver: role.name === "pr_visitor",
    isCollaborator: role.name === "pr_worker",
  };
}

export { useProjectPermissions, usePlanzonePermissions };
